// Slide 7 - IDF (Invention Disclosure Form)
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

export default function Slide7() {
  const images = ["/idf/idf_1.png", "/idf/idf_2.png", "/idf/idf_3.png"];
  const [idx, setIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [portalEl, setPortalEl] = useState(null);
  const url = "https://idf.lextria.in/";

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
      if (e.key === "ArrowLeft")
        setIdx((s) => (s - 1 + images.length) % images.length);
      if (e.key === "ArrowRight") setIdx((s) => (s + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, images.length]);

  useEffect(() => {
    const el = document.createElement("div");
    document.body.appendChild(el);
    setPortalEl(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
      setPortalEl(null);
    };
  }, []);

  return (
    <section className="w-full max-w-[1400px] mx-auto relative z-10 px-3 sm:px-4 md:px-6 py-6 sm:py-8 md:py-12 lg:py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-12 items-start">
        <div className="space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-6 bg-white/6 bg-clip-padding backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 md:p-6 lg:p-8 xl:p-12 border border-white/12 shadow-2xl">
          <div>
            <span className="inline-block px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-white/12 rounded-full text-white text-xs sm:text-sm uppercase tracking-widest font-semibold border border-white/20">
              Product
            </span>
            <h2
              className="text-white font-black mt-2 sm:mt-3 md:mt-4 leading-tight"
              style={{ fontSize: "clamp(32px, 6.2vw, 58px)" }}
            >
              IDF
            </h2>
            <div
              className="text-white/75 mt-0.5 sm:mt-1"
              style={{ fontSize: "clamp(13px, 1.4vw, 15px)" }}
            >
              Invention Disclosure Form — structured intake and dashboard
              tracking.
            </div>
          </div>

          <p
            className="text-white/90 leading-relaxed"
            style={{ fontSize: "clamp(14px, 1.9vw, 18px)" }}
          >
            Upload your patent details once and be redirected to a dashboard
            where you can track status, view patentability insights, and
            maintain clear accountability with the funding agency.
          </p>

          <ul className="text-white/80 space-y-1.5 sm:space-y-2 md:space-y-3">
            <li className="flex items-start gap-2 sm:gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mt-0.5 sm:mt-1 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-xs sm:text-sm md:text-sm">
                Easy, structured submission format for users and agencies.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 mt-1 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-xs sm:text-sm md:text-sm">
                Dashboard shows status, patentability signals, and next steps.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 mt-1 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-xs sm:text-sm md:text-sm">
                Clear accountability and tracking between users and funding
                agencies.
              </span>
            </li>
            {/* CRM delivery note moved below Visit IDF button to match AutoIP tag style */}
          </ul>

          <div className="mt-2 sm:mt-3 md:mt-4 lg:mt-6 flex flex-col items-start gap-2 sm:gap-3">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 sm:gap-3 bg-white text-slate-900 px-3 sm:px-4 md:px-5 lg:px-6 py-1.5 sm:py-2 md:py-2.5 lg:py-3 rounded-lg font-semibold text-sm sm:text-base shadow hover:shadow-lg"
            >
              Visit IDF
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-slate-700"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8"
                />
              </svg>
            </a>

            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm md:text-sm">
              <div className="text-white/70 font-semibold">
                Similar CRM development - delivery in
              </div>
              <div className="relative flex items-center w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4">
                <span className="absolute inline-flex h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-3.5 md:w-3.5 rounded-full bg-white/60 opacity-75 animate-ping" />
                <span className="relative inline-block h-1.5 w-1.5 sm:h-2 sm:w-2 md:h-2.5 md:w-2.5 rounded-full bg-white shadow" />
              </div>
              <div className="text-white font-medium">
                <span className="keyword">under 2 weeks</span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative h-auto flex flex-col items-center lg:items-end w-full">
          <div className="w-full max-w-[820px] bg-white/6 backdrop-blur-sm rounded-xl sm:rounded-2xl p-2 sm:p-3 md:p-4 border border-white/12 shadow-xl">
            <div className="relative w-full h-48 sm:h-64 md:h-80 lg:h-96 xl:h-[460px] rounded-lg overflow-hidden bg-white/8 flex items-center justify-center">
              <img
                src={images[idx]}
                alt={`IDF screenshot ${idx + 1}`}
                className="w-full h-full object-contain transition-transform duration-300"
              />

              {!expanded && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIdx((s) => (s - 1 + images.length) % images.length);
                    }}
                    aria-label="Previous image"
                    className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 bg-black/60 p-2 sm:p-3 rounded-full hover:bg-black/50 ring-1 ring-white/10"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 18l-6-6 6-6"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIdx((s) => (s + 1) % images.length);
                    }}
                    aria-label="Next image"
                    className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 bg-black/60 p-2 sm:p-3 rounded-full hover:bg-black/50 ring-1 ring-white/10"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 6l6 6-6 6"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(true);
                    }}
                    aria-label="Expand image"
                    className="absolute right-3 bottom-3 bg-black/60 p-1.5 sm:p-2 rounded-md hover:bg-black/50 ring-1 ring-white/10"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"
                      />
                    </svg>
                  </button>
                </>
              )}

              {portalEl &&
                expanded &&
                createPortal(
                  <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-6"
                    onClick={() => setExpanded(false)}
                  >
                    <button
                      onClick={() => setExpanded(false)}
                      aria-label="Close full screen"
                      className="absolute top-6 right-6 bg-white/90 p-3 rounded-md hover:bg-white/80 ring-1 ring-black/10 shadow-2xl"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5 text-slate-900"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIdx((s) => (s - 1 + images.length) % images.length);
                      }}
                      aria-label="Previous image"
                      className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/10 p-3 rounded-full hover:bg-white/16"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6 text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 18l-6-6 6-6"
                        />
                      </svg>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIdx((s) => (s + 1) % images.length);
                      }}
                      aria-label="Next image"
                      className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/10 p-3 rounded-full hover:bg-white/16"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6 text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 6l6 6-6 6"
                        />
                      </svg>
                    </button>

                    <img
                      src={images[idx]}
                      alt={`IDF fullscreen ${idx + 1}`}
                      onClick={(e) => e.stopPropagation()}
                      className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
                    />
                  </div>,
                  portalEl,
                )}
            </div>

            <div className="mt-2 sm:mt-3 md:mt-4 flex items-center justify-center gap-2 sm:gap-3 md:gap-4">
              {images.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setIdx(i)}
                  className={`w-16 h-12 sm:w-20 sm:h-14 md:w-24 md:h-16 lg:w-28 lg:h-20 rounded-lg overflow-hidden border ${i === idx ? "border-white/40 scale-105" : "border-white/8"} bg-white/8 transform transition-all`}
                >
                  <img
                    src={src}
                    alt={`idf-thumb-${i}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
